import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TrendingUp, ArrowRight } from "lucide-react";
import { CalculatorContent } from "./types";

interface CalculatorResultsProps {
  content: CalculatorContent;
  giftValue: number;
  currentAge: number;
  livretAAt18: number;
  livretAAt25: number;
  livretANow: number;
  legatiaAt18: number;
  legatiaAt25: number;
  legatiaNow: number;
}

const CalculatorResults = ({
  content,
  giftValue,
  currentAge,
  livretAAt18,
  livretAAt25,
  livretANow,
  legatiaAt18,
  legatiaAt25,
  legatiaNow
}: CalculatorResultsProps) => {
  const maxValue = Math.max(livretANow, legatiaNow);
  const difference = legatiaNow - livretANow;
  const differencePercentage = livretANow > 0 ? ((difference / livretANow) * 100).toFixed(0) : 0;

  const ComparisonRow = ({ 
    age, 
    livretValue, 
    legatiaValue 
  }: { 
    age: string; 
    livretValue: number; 
    legatiaValue: number 
  }) => {
    const rowDifference = legatiaValue - livretValue;
    const maxValueRow = Math.max(livretValue, legatiaValue);
    const livretPercentage = (livretValue / maxValueRow) * 100;
    const legatiaPercentage = (legatiaValue / maxValueRow) * 100;
    
    return (
      <div className="space-y-4 pb-6 border-b border-border last:border-0">
        {/* Age Header */}
        <div className="text-sm font-semibold text-foreground/70">{age}</div>
        
        {/* Livret A Row */}
        <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-3">
          <div className="w-full md:w-28 text-sm font-medium text-muted-foreground flex items-center gap-1.5">
            <span>📊</span> Livret A
          </div>
          
          <div className="flex items-center gap-2 md:gap-3 flex-1 w-full">
            <Progress 
              value={livretPercentage} 
              className="flex-1 h-6 bg-gray-100 [&>div]:bg-gray-400"
              aria-label={`Livret A ${age}: €${livretValue.toLocaleString()}`}
            />
            <div className="text-right font-bold text-foreground whitespace-nowrap w-20 md:w-24">
              €{livretValue.toLocaleString()}
            </div>
          </div>
        </div>
        
        {/* Legatia ETF Row */}
        <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-3">
          <div className="w-full md:w-28 text-sm font-medium text-legatia-green flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" /> Legatia ETF
          </div>
          
          <div className="flex items-center gap-2 md:gap-3 flex-1 w-full">
            <Progress 
              value={legatiaPercentage} 
              className="flex-1 h-6 bg-emerald-50 [&>div]:bg-legatia-green [&>div]:animate-pulse-slow"
              aria-label={`Legatia ETF ${age}: €${legatiaValue.toLocaleString()}`}
            />
            <div className="text-right font-bold text-foreground whitespace-nowrap w-20 md:w-24">
              €{legatiaValue.toLocaleString()}
            </div>
            <div className="hidden md:block w-28">
              <span className="inline-flex items-center gap-1 bg-legatia-green/10 text-legatia-green border border-legatia-green/30 px-2.5 py-1 rounded-full text-xs font-bold">
                💰 +€{rowDifference.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        
        {/* Mobile difference badge */}
        <div className="md:hidden flex justify-end">
          <span className="inline-flex items-center gap-1 bg-legatia-green/10 text-legatia-green border border-legatia-green/30 px-2.5 py-1 rounded-full text-xs font-bold">
            💰 +€{rowDifference.toLocaleString()}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl md:text-2xl font-bold text-center">{content.yourCapital}</h3>
      
      {/* Single Comparison Card */}
      <Card className="bg-card border-2 border-legatia-green/20 shadow-lg">
        <CardHeader className="pb-4 border-b border-border">
          <div className="grid grid-cols-2 gap-4 items-center text-center">
            <div className="space-y-1">
              <div className="text-base font-bold text-foreground/70 flex items-center justify-center gap-2">
                <span>📊</span> {content.livretA}
              </div>
              <p className="text-xs text-muted-foreground">Taux: 1,5% / an</p>
            </div>
            
            <div className="space-y-1">
              <div className="text-base font-bold text-legatia-green flex items-center justify-center gap-2">
                <TrendingUp className="h-4 w-4" /> {content.legatia}
              </div>
              <p className="text-xs text-legatia-green/80">Rendement: 8% / an</p>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-6 space-y-6">
          <ComparisonRow 
            age={content.at18} 
            livretValue={livretAAt18} 
            legatiaValue={legatiaAt18} 
          />
          <ComparisonRow 
            age={content.at25} 
            livretValue={livretAAt25} 
            legatiaValue={legatiaAt25} 
          />
          <ComparisonRow 
            age={`${content.now} (${currentAge} ans)`} 
            livretValue={livretANow} 
            legatiaValue={legatiaNow} 
          />
        </CardContent>
      </Card>

      {/* Difference Badge */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-3 bg-gradient-to-r from-legatia-green to-emerald-500 text-white px-6 py-4 rounded-full shadow-xl animate-pulse">
          <span className="text-sm font-medium">💰 DIFFÉRENCE TOTALE</span>
          <ArrowRight className="h-5 w-5" />
          <span className="text-2xl font-bold">+€{difference.toLocaleString()}</span>
          <span className="text-sm bg-white/20 px-3 py-1 rounded-full">+{differencePercentage}%</span>
        </div>
      </div>

      {/* Explanations Accordion */}
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="explanation" className="border border-legatia-green/20 rounded-lg px-4">
          <AccordionTrigger className="text-legatia-green hover:text-legatia-green/80">
            📊 Pourquoi cette différence ?
          </AccordionTrigger>
          <AccordionContent className="text-sm text-gray-700 space-y-3 pt-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="mb-2">
                <strong>Livret A :</strong> Épargne réglementée française avec un taux de 1,5% annuel garanti par l'État. Sécurisé mais rendement faible.
              </p>
              <p>
                <strong>Legatia ETF :</strong> Investissement diversifié dans des fonds indiciels (ETF) avec un rendement moyen historique de 8% annuel. Les ETF permettent d'investir dans un panier d'actions mondiales avec une gestion passive et des frais réduits.
              </p>
            </div>
            <p className="text-xs text-gray-500 italic">
              Les performances passées ne préjugent pas des performances futures. Les ETF comportent des risques de perte en capital.
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Integrated CTA */}
      <div className="bg-gradient-to-r from-legatia-green/10 to-emerald-50 border-2 border-legatia-green/30 rounded-xl p-6 text-center space-y-4">
        <h4 className="text-xl font-bold text-legatia-green">Commencez dès aujourd'hui</h4>
        <p className="text-gray-700">Ne laissez pas l'argent dormir. Investissez pour l'avenir de votre enfant.</p>
        <Button 
          className="bg-legatia-green hover:bg-legatia-green/90 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105"
          size="lg"
          onClick={() => window.location.href = "https://calendly.com/legatia/15min"}
        >
          Rejoindre Legatia <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default CalculatorResults;
